import React from "react";
import axios, { AxiosError } from "axios";
import { ProgressNotificationHandle, isErrorMessage } from "@sscs/types";
import { Toast } from "@sscs/ui";
import type { StoreType } from "Store/store";
import {
  getRouteBasename,
  isShareableReportRoute,
  resetStoreThunk,
  sessionExpiredAction,
} from "Utils";
import { getCsrfToken, getSharedToken } from "./baseApiUtils";
import { NotificationDownloadMessages } from "./common";

const basename = getRouteBasename();

// https://redux.js.org/faq/code-structure#how-can-i-use-the-redux-store-in-non-component-files
let store: StoreType;

export const injectStore = (_store: StoreType) => {
  store = _store;
};

export const axiosInstance = axios.create({
  baseURL: basename ? `${basename}${API_ENDPOINT}` : API_ENDPOINT,
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["X-CSRFToken"] = getCsrfToken();

    if (isShareableReportRoute()) {
      config.headers["Shared-Token"] = getSharedToken();
    }

    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    if (error.response?.status === 401) {
      store.dispatch(resetStoreThunk());
      store.dispatch(sessionExpiredAction());
    }

    return Promise.reject(error);
  },
);

export const downloadFile = async (
  downloadUrl: string,
  fileName: string,
  downloadMessages: NotificationDownloadMessages,
): Promise<void> => {
  let progressHandle: ProgressNotificationHandle | undefined;
  const controller = new AbortController();

  try {
    const result = await axiosInstance({
      url: downloadUrl,
      method: "GET",
      responseType: "blob",
      signal: controller.signal,
      onDownloadProgress: (progressEvent) => {
        const progress = progressEvent.loaded / (progressEvent.total ?? 1);

        // Check that file is still downloading
        // On fast networks file can be downloaded before onDownloadProgress event is triggered
        if (progress < 1) {
          const percent = progress * 100;

          // Lazy-create the progress toast on the first progress event.
          if (progressHandle === undefined) {
            progressHandle = Toast.progress({
              kind: "export",
              title: downloadMessages.progress,
              percent,
              onCancel: () => controller.abort(),
            });
          } else {
            progressHandle.update({ percent });
          }
        }
      },
    });

    // Trigger file save dialog
    const blob = new Blob([result.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);

    if (progressHandle) {
      progressHandle.done();
    } else {
      // Notify user that download finished even if there was no time to show download progress
      Toast.show({
        title: downloadMessages.success,
        type: "success",
        props: {
          autoClose: 3000,
        },
      });
    }
  } catch (axiosError) {
    if (axios.isCancel(axiosError)) {
      // the progress toast is dismissed by the Cancel handler
      Toast.show({
        title: downloadMessages.cancel,
        type: "info",
      });
    } else {
      const error = axiosError as AxiosError<Blob>;
      const errorDataText = await error.response?.data?.text();
      const errorData = errorDataText ? JSON.parse(errorDataText) : undefined;
      const errorMessage =
        errorData && isErrorMessage({ data: errorData })
          ? errorData.error
          : downloadMessages.error(error);

      if (progressHandle) {
        progressHandle.error({ title: errorMessage });
      } else {
        Toast.show({
          title: errorMessage,
          type: "error",
        });
      }
    }
  }
};

type UploadResult = {
  resultType: "ERROR" | "SUCCESS" | "CANCEL";
  status?: number;
  data: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

const FileCountOrFileName = ({
  count,
  fileName,
}: {
  count: number;
  fileName?: string;
}): React.JSX.Element => {
  if (!fileName) {
    return (
      <>
        {count} file{count !== 1 ? "s" : ""}
      </>
    );
  } else if (count !== 1) {
    return <>{count} files</>;
  } else {
    return (
      <>
        file <strong>{fileName}</strong>
      </>
    );
  }
};

export const uploadFile = async (
  url: string,
  data: FormData,
  count: number,
): Promise<UploadResult> => {
  let progressHandle: ProgressNotificationHandle | undefined;
  const file = data.get("file");
  const fileName = file && typeof file !== "string" ? file.name : "";
  const controller = new AbortController();

  try {
    const result = await axiosInstance.request({
      method: "POST",
      url: url,
      data: data,
      signal: controller.signal,
      onUploadProgress: (progressEvent) => {
        const progress = progressEvent.loaded / (progressEvent.total ?? 1);

        // Check that file is still uploading
        // On fast networks file can be uploaded before onUploadProgress event is triggered
        if (progress < 1) {
          const percent = progress * 100;

          // Lazy-create the progress toast on the first progress event.
          if (progressHandle === undefined) {
            progressHandle = Toast.progress({
              kind: "upload",
              title: (
                <>
                  Uploading{" "}
                  <FileCountOrFileName count={count} fileName={fileName} />
                </>
              ),
              percent,
              onCancel: () => controller.abort(),
              props: { position: "bottom-right" },
            });
          } else {
            progressHandle.update({ percent });
          }
        } else {
          // Upload finished; server is now moving the file. Disable Cancel.
          progressHandle?.update({
            title: (
              <>
                Moving <FileCountOrFileName count={count} fileName={fileName} />{" "}
                to cloud storage
              </>
            ),
            cancelDisabled: true,
          });
        }
      },
    });

    if (progressHandle) {
      progressHandle.done();
    } else if (count > 0) {
      // Notify user that upload finished even if there was no time to show upload progress
      Toast.show({
        title: (
          <>
            Uploaded <FileCountOrFileName count={count} fileName={fileName} />
          </>
        ),
        type: "success",
        props: {
          autoClose: 3000,
          position: "bottom-right",
        },
      });
    }

    return { resultType: "SUCCESS", data: result.data };
  } catch (axiosError) {
    if (axios.isCancel(axiosError)) {
      // the progress toast is dismissed by the Cancel handler
      Toast.show({
        title: (
          <>
            Canceled upload for{" "}
            <FileCountOrFileName count={count} fileName={fileName} />
          </>
        ),
        type: "info",
        props: {
          position: "bottom-right",
        },
      });

      // When user cancels upload it is still considered as a success since it was intentional action
      return { resultType: "CANCEL", data: "Request cancelled" };
    } else {
      const err = axiosError as AxiosError;
      const errorMessage = isErrorMessage(err.response)
        ? err.response.data.error
        : "Unknown error";

      if (progressHandle) {
        progressHandle.error({
          title: errorMessage,
          props: { position: "bottom-right" },
        });
      } else {
        Toast.show({
          title: errorMessage,
          type: "error",
        });
      }

      return {
        resultType: "ERROR",
        status: err.response?.status,
        data: err.response?.data,
      };
    }
  }
};
